import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { Form } from 'semantic-ui-react';
import { z } from 'zod';
import {
    standardizedLabelNameSchema,
    standardizedLabelScopeSchema,
    standardizedLabelTagsSchema,
    StandardizedLabelType,
} from '../../../../../schemas/standardizedLabel.schemas';
import { getOption } from '../../../../../components/admin/task/utils';
import {
    STANDARDIZED_LABEL_SCOPE,
    STANDARDIZED_LABEL_SCOPES,
    STANDARDIZED_LABEL_TAGS,
} from '../../../../../constnats/standardizedLabels';

const formSchema = z.object({
    name: standardizedLabelNameSchema,
    scope: standardizedLabelScopeSchema,
    tags: standardizedLabelTagsSchema,
});

type FormDataType = z.infer<typeof formSchema>;

type StandardizedLabelFormPropsType = {
    standardizedLabel?: StandardizedLabelType;
    onSave: (data: FormDataType) => void;
    onCancel?: () => void;
};

function StandardizedLabelForm({ standardizedLabel, onSave, onCancel }: StandardizedLabelFormPropsType) {
    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<FormDataType>({
        defaultValues: standardizedLabel ?? {
            name: '',
            scope: STANDARDIZED_LABEL_SCOPE.NOT_SPECIFIED,
            tags: [],
        },
        resolver: zodResolver(formSchema),
    });

    const onSubmit = handleSubmit((data) => {
        onSave({ name: data.name.trim(), scope: data.scope, tags: data.tags });
    });

    return (
        <Form onSubmit={onSubmit}>
            <Controller
                name="name"
                control={control}
                render={({ field: { value, onChange } }) => (
                    <Form.Input
                        disabled={isSubmitting}
                        value={value}
                        onChange={(_, data) => onChange(data.value)}
                        fluid
                        label="Name"
                        placeholder="COGS (Cost of Goods Sold)"
                        error={errors.name?.message}
                    />
                )}
            />
            <Controller
                name="scope"
                control={control}
                render={({ field: { value, onChange } }) => (
                    <Form.Dropdown
                        disabled={isSubmitting}
                        value={value}
                        onChange={(_, data) => onChange(data.value)}
                        label="Scope"
                        selection
                        search
                        options={STANDARDIZED_LABEL_SCOPES.map((s) => getOption(s, s))}
                        error={errors.scope?.message}
                    />
                )}
            />
            <Controller
                name="tags"
                control={control}
                render={({ field: { value, onChange } }) => (
                    <Form.Dropdown
                        disabled={isSubmitting}
                        value={value}
                        onChange={(_, data) => onChange(data.value)}
                        fluid
                        label="Tags"
                        placeholder="Expense, Financial, etc."
                        error={errors.tags?.message}
                        multiple
                        selection
                        search
                        options={STANDARDIZED_LABEL_TAGS.map((t) => getOption(t, t))}
                    />
                )}
            />

            <Form.Group className="flex flex-justify-space-between">
                <Form.Button primary type="submit" disabled={isSubmitting}>
                    Save
                </Form.Button>

                <Form.Button type="button" onClick={onCancel} disabled={isSubmitting}>
                    Cancel
                </Form.Button>
            </Form.Group>
        </Form>
    );
}

export default StandardizedLabelForm;
